/**
 * Hook for fetching explore categories with react-query caching
 */
import { useQuery } from '@tanstack/react-query';
import { ExploreCategoryApi } from '../Model/Explore/ExploreCategory';
import { isPublished } from '../Utils/isPublished';
import { getAllExploreCategoriesV3, resolveCategoryId } from '../Model/Explore/exploreCategoryTreeV3';

export type ExploreCategoryType = 'movement' | 'mental' | 'nutrition' | 'academy';

// Exponential backoff capped at 8 s. With retry: 3 this becomes ~2s / 4s / 8s.
// Anything past that and the user is better off seeing the error state than waiting longer.
const retryDelay = (attempt: number) => Math.min(1000 * 2 ** attempt, 8_000);

export function useExploreCategories(categoryType: ExploreCategoryType) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['exploreCategoriesV3', 'children', categoryType],
    queryFn: async () => {
      // Resolve the root identifier to its UUID and return its direct children
      // (V3 has no slug lookup / `root` flag — derive from the flat set).
      const all = await getAllExploreCategoriesV3();
      const parentId = resolveCategoryId(all, categoryType);
      if (!parentId) return [];
      return all.filter((c) => (c.parentIds ?? []).includes(parentId)).filter(isPublished);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes cache
    retry: 3,
    retryDelay,
  });

  const categories = data ?? [];

  return { content: categories, categories, isLoading, error, refetch };
}

/**
 * Fetches the user's company-private root category, if any.
 * Used by the home-screen `CompanyAreaCard` tile.
 *
 * Previously this lived in CompanyAreaCard as a bespoke `useEffect + setState`,
 * which had no retry, no shared cache, and silently swallowed network failures.
 * Moving it into React Query gives us retry/backoff (so transient backend stalls
 * recover), a 5-minute stale window, and a proper error state the UI can surface.
 */
export function useCompanyRootCategories(gymId: string | undefined) {
  const enabled = Boolean(gymId);
  return useQuery({
    queryKey: ['exploreCompanyRootCategories', gymId ?? null],
    queryFn: () => ExploreCategoryApi.find({ root: true }),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 3,
    retryDelay,
  });
}
